import React, { useState, useEffect, useContext, useRef } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { FaEdit, FaTrash } from 'react-icons/fa';
import { toast } from 'react-toastify';
import Alert from '../common/Alert';
import IconButton from '../common/IconButton';
import useApi from '../../hooks/useApi';
import api from '../../api';
import CommunityIdentity from '../common/CommunityIdentity';
import 'animate.css';

/**
 * Component to manage and display community notices
 */
function NoticeBoard() {
  const { session } = useContext(AuthContext);
  const { roles, communityId } = session || { roles: ['GUEST'], communityId: null };
  const isSuperAdmin = roles.includes('SUPER_ADMIN');
  const isAdmin = roles.includes('ADMIN');
  const isUser = roles.includes('USER') || isAdmin || isSuperAdmin;
  const { get, post, put } = useApi();
  console.log('NoticeBoard - useApi destructured:', { get, post, put });
  const [notices, setNotices] = useState([]);
  const [communities, setCommunities] = useState([]);
  const [selectedCommunityId, setSelectedCommunityId] = useState(isSuperAdmin ? '' : communityId || '');
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({ title: '', content: '', communityId: isSuperAdmin ? '' : communityId || '', endDate: '' });
  const [file, setFile] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const fileInputRef = useRef(null);

  console.log('NoticeBoard - session:', { roles, communityId, isSuperAdmin, isAdmin, isUser, selectedCommunityId });

  useEffect(() => {
    if (isSuperAdmin) {
      fetchCommunities();
    }
    fetchNotices();
  }, [selectedCommunityId]);

  const fetchCommunities = async () => {
    try {
      console.log('NoticeBoard - Fetching communities from /api/communities/active');
      const response = await get('/api/communities/active');
      console.log('NoticeBoard - Communities response:', response);
      if (!Array.isArray(response)) {
        throw new Error('Invalid communities response format');
      }
      setCommunities(response);
    } catch (err) {
      console.error('NoticeBoard - Fetch communities error:', err);
      setError('Failed to load communities.');
      toast.error('Failed to load communities.');
    }
  };

  const fetchNotices = async () => {
    try {
      console.log('NoticeBoard - Fetching notices, communityId:', selectedCommunityId || 'All Communities (SUPER_ADMIN)', 'params:', (isSuperAdmin && selectedCommunityId) || (!isSuperAdmin && communityId) ? { communityId: selectedCommunityId || communityId } : {});
      const params = (isSuperAdmin && selectedCommunityId) || (!isSuperAdmin && communityId) ? { communityId: selectedCommunityId || communityId } : {};
      const response = await get('/api/notices', { params });
      console.log('NoticeBoard - Fetched notices:', response);
      setNotices(response || []);
      setError('');
    } catch (err) {
      console.error('NoticeBoard - Fetch error:', err);
      setError('Failed to fetch notices: ' + err.message);
      toast.error('Failed to fetch notices.');
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png'];
      if (!allowedTypes.includes(selectedFile.type)) {
        setError('Only PDF, JPEG, or PNG files are allowed.');
        toast.error('Only PDF, JPEG, or PNG files are allowed.');
        setFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }
      if (selectedFile.size > 500 * 1024) {
        setError('File size exceeds 500KB limit.');
        toast.error('File size exceeds 500KB limit.');
        setFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }
      setFile(selectedFile);
      setError('');
    }
  };

  const handleCommunityChange = (e) => {
    const newCommunityId = e.target.value;
    console.log('NoticeBoard - Community changed to:', newCommunityId || 'All Communities');
    setSelectedCommunityId(newCommunityId);
    setFormData({ ...formData, communityId: newCommunityId });
  };

  const handleClear = () => {
    setFormData({ title: '', content: '', communityId: isSuperAdmin ? '' : communityId || '', endDate: '' });
    setFile(null);
    setError('');
    setEditingId(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (isSuperAdmin) {
      setSelectedCommunityId('');
    }
    toast.info('Form cleared');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if ((isAdmin && !communityId) || (isSuperAdmin && !formData.communityId && !editingId)) {
      setError('Please select a community for the notice.');
      toast.error('Please select a community.');
      return;
    }
    if (!formData.endDate) {
      setError('Please provide an end date for the notice.');
      toast.error('Please provide an end date.');
      return;
    }
    // Validate that endDate is not in the past
    const today = new Date().toISOString().split('T')[0];
    if (formData.endDate < today) {
      setError('End date cannot be in the past.');
      toast.error('End date cannot be in the past.');
      return;
    }
    try {
      const form = new FormData();
      form.append('notice', new Blob([JSON.stringify(formData)], { type: 'application/json' }));
      if (file) {
        console.log('Uploading file:', file.name);
        form.append('file', file);
      }
      if (editingId) {
        console.log('NoticeBoard - Updating notice:', { id: editingId, ...formData });
        const response = await put(`/api/notices/${editingId}`, form);
        setNotices(
          notices.map((notice) =>
            notice.id === editingId
              ? { ...notice, ...formData, updatedAt: new Date().toISOString(), attachmentPath: response.attachmentPath }
              : notice
          )
        );
        console.log('NoticeBoard - Updated notice response:', response);
        toast.success('Notice updated successfully');
        setEditingId(null);
        setShowForm(false);
        setFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } else {
        console.log('NoticeBoard - Creating notice:', formData);
        const response = await post('/api/notices', form);
        setNotices([...notices, response]);
        console.log('NoticeBoard - Created notice response:', response);
        toast.success('Notice created successfully');
        setFormData({ title: '', content: '', communityId: isSuperAdmin ? '' : communityId || '', endDate: '' });
        setFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        setError('');
        setShowForm(false);
      }
    } catch (err) {
      console.error('NoticeBoard - Submit error:', err);
      const errorMessage = err.response?.data?.message || `Failed to ${editingId ? 'update' : 'create'} notice.`;
      setError(errorMessage);
      toast.error(errorMessage);
    }
  };

  const handleEdit = (notice) => {
    setShowForm(true);
    setFormData({ 
      title: notice.title, 
      content: notice.content, 
      communityId: notice.communityId, 
      endDate: notice.endDate ? notice.endDate.split('T')[0] : ''
    });
    setEditingId(notice.id);
    if (isSuperAdmin) {
      setSelectedCommunityId(notice.communityId);
    }
    setFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (id) => {
    try {
      console.log('NoticeBoard - Deleting notice ID type and value:', typeof id, id);
      const response = await api.delete(`/api/notices/${id}`);
      setNotices(notices.filter((notice) => notice.id !== id));
      console.log('NoticeBoard - Deleted notice response:', response.data);
      toast.success('Notice deleted successfully');
      setError('');
    } catch (err) {
      console.error('NoticeBoard - Delete error:', err);
      setError(`Failed to delete notice: ${err.message}`);
      toast.error('Failed to delete notice.');
    }
  };

  const handleDownload = async (attachmentPath) => {
    try {
      console.log('NoticeBoard - Downloading file:', attachmentPath);
      const response = await api.get(attachmentPath, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = attachmentPath.split('/').pop();
      const cleanFilename = filename.includes('_') ? filename.substring(filename.indexOf('_') + 1) : filename;
      link.setAttribute('download', cleanFilename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('File downloaded successfully');
    } catch (err) {
      console.error('NoticeBoard - Download error:', err);
      setError(`Failed to download file: ${err.message}`);
      toast.error('Failed to download file.');
    }
  };

  const getNoticeCreatorFirstName = notice => {
    const creator = notice?.createdByName?.trim() || notice?.createdByUsername?.trim();
    return creator ? creator.split(/\s+/)[0] : 'Creator unavailable';
  };

  if (!isUser) {
    return <Alert type="warning" message="Access denied. You must be logged in to view notices." />;
  }

  return (
    <div className="container mt-5 animate__animated animate__fadeIn">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="mb-0">Notice Board</h2>
        {(isAdmin || isSuperAdmin) && <button type="button" className="btn btn-primary" onClick={() => { setEditingId(null); setShowForm(value => !value); }}>{showForm ? 'Close form' : 'Create notice'}</button>}
      </div>
      {error && <Alert type="danger" message={error} />}

      {(isAdmin || isSuperAdmin) && showForm && (
        <div className="card shadow mb-4">
          <div className="card-body">
            <h5 className="card-title">{editingId ? 'Edit Notice' : 'Create Notice'}</h5>
            <form onSubmit={handleSubmit}>
              {isSuperAdmin && (
                <div className="mb-3">
                  <label htmlFor="communityFilter" className="form-label">
                    Select Community
                  </label>
                  <select
                    id="communityFilter"
                    className="form-select"
                    name="communityId"
                    value={formData.communityId}
                    onChange={handleChange}
                    disabled={communities.length === 0 || editingId}
                    required
                  >
                    <option value="">Select a Community</option>
                    {communities.map((community) => (
                      <option key={community.id} value={community.id}>
                        {community.displayName}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className="mb-3">
                <label htmlFor="title" className="form-label">
                  Title
                </label>
                <input
                  type="text"
                  className="form-control"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="mb-3">
                <label htmlFor="content" className="form-label">
                  Content
                </label>
                <textarea
                  className="form-control"
                  id="content"
                  name="content"
                  value={formData.content}
                  onChange={handleChange}
                  rows="5"
                  required
                ></textarea>
              </div>
              <div className="mb-3">
                <label htmlFor="endDate" className="form-label">
                  End Date
                </label>
                <input
                  type="date"
                  className="form-control"
                  id="endDate"
                  name="endDate"
                  value={formData.endDate}
                  onChange={handleChange}
                  required
                  min={new Date().toISOString().split('T')[0]} // Restrict to today or future dates
                />
              </div>
              <div className="mb-3">
                <label htmlFor="file" className="form-label">
                  Attachment (PDF, JPEG, PNG, max 500KB)
                </label>
                <input
                  type="file"
                  className="form-control"
                  id="file"
                  name="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={handleFileChange}
                  ref={fileInputRef}
                />
              </div>
              <div className="d-flex gap-2">
                <button type="submit" className="btn btn-primary flex-grow-1">
                  {editingId ? 'Update Notice' : 'Post Notice'}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary flex-grow-1"
                  onClick={handleClear}
                >
                  Clear
                </button>
                {editingId && (
                  <button
                    type="button"
                    className="btn btn-secondary flex-grow-1"
                    onClick={() => {
                      setEditingId(null);
                      setFormData({ title: '', content: '', communityId: isSuperAdmin ? '' : communityId || '', endDate: '' });
                      setFile(null);
                      if (fileInputRef.current) {
                        fileInputRef.current.value = '';
                      }
                    }}
                  >
                    Cancel
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

      {isSuperAdmin && (
        <div className="mb-3">
          <label htmlFor="viewCommunityFilter" className="form-label">
            View Notices for Community
          </label>
          <select
            id="viewCommunityFilter"
            className="form-select"
            value={selectedCommunityId}
            onChange={handleCommunityChange}
            disabled={communities.length === 0}
          >
            <option value="">All Communities</option>
            {communities.map((community) => (
              <option key={community.id} value={community.id}>
                {community.displayName}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="row">
        {notices.length === 0 ? (
          <div className="col-12">
            <Alert type="info" message="No notices available." />
          </div>
        ) : (
          notices.map((notice) => (
            <div key={notice.id} className="col-md-6 mb-4">
              <div className="card shadow notice-page-card">
                <div className="card-body">
                  <div className="notice-page-meta">
                    <span>{notice.status}</span>
                    <small>Created by {getNoticeCreatorFirstName(notice)}</small>
                  </div>
                  <h5 className="card-title">{notice.title}</h5>
                  <p className="card-text">{notice.content}</p>
                  {notice.attachmentPath && (
                    <p>
                      <button
                        className="btn btn-link"
                        onClick={() => handleDownload(notice.attachmentPath)}
                      >
                        Download Attachment ({notice.attachmentType})
                      </button>
                      {console.log('Rendering attachment path:', notice.attachmentPath)}
                    </p>
                  )}
                  <small className="text-muted">
                    Posted on {new Date(notice.createdAt).toLocaleDateString()}
                    {notice.updatedAt &&
                      ` | Updated on ${new Date(notice.updatedAt).toLocaleDateString()}`}
                    {notice.endDate &&
                      ` | Expires on ${new Date(notice.endDate).toLocaleDateString()}`}
                    {isSuperAdmin && notice.community && (
                      <> {' | Community: '}<CommunityIdentity community={notice.community} size="xs" compact /></>
                    )}
                  </small>
                  {(isAdmin || isSuperAdmin) && (
                    <div className="mt-2">
                      <IconButton
                        icon={<FaEdit />}
                        tooltip="Edit"
                        variant="warning"
                        onClick={() => handleEdit(notice)}
                        className="me-2"
                      />
                      <IconButton
                        icon={<FaTrash />}
                        tooltip="Delete"
                        variant="danger"
                        onClick={() => {
                          console.log('Delete button clicked for ID:', notice.id);
                          handleDelete(notice.id);
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default NoticeBoard;
